<template>
  <picture v-if="media && fallbackSrc" :key="media.documentId" :class="pictureClass" :width="mediaWidht" :height="mediaHeight">
    <source v-if="avifSrcset" type="image/avif" :sizes="sizes" :srcset="avifSrcset" />
    <source v-if="primarySrcset" :type="`image/${props.format}`" :sizes="sizes" :srcset="primarySrcset" />
    <img
      :src="fallbackSrc"
      :alt="altText"
      :class="imgClass"
      :width="mediaWidht"
      :height="mediaHeight"
      :loading="loading"
      :fetchpriority="fetchpriority"
      @load="handleLoad"
    />
  </picture>
  <div v-else></div>
</template>

<script setup lang="ts">
  import type { StrapiMedia } from '~/types/strapi';
  import { buildImgproxyUrl, buildResizeOption, clampDimensions, type ImgproxyFormat } from '#shared/imgproxy';

  interface Props {
    media: StrapiMedia | null | undefined;
    alt?: string;
    format?: ImgproxyFormat;
    quality?: number;
    loading?: 'lazy' | 'eager';
    sizes?: string;
    width?: number | string;
    height?: number | string;
    imageClass?: string;
    fetchpriority?: 'high' | 'low' | 'auto';
    // 'cover' = imgproxy rs:fill, container fills, image cropped to fit (default)
    // 'contain' = imgproxy rs:fit, container width-driven, image preserves aspect
    fit?: 'cover' | 'contain';
  }

  const props = withDefaults(defineProps<Props>(), {
    alt: '',
    format: 'webp',
    quality: 80,
    loading: 'lazy',
    sizes: '100vw',
    fetchpriority: 'auto',
    fit: 'cover',
  });

  // The public media host (e.g. https://media.herbertseidl.at). Public runtime
  // config because URLs are now built in the browser as well as during SSR.
  const runtimeConfig = useRuntimeConfig();
  const imgproxyHost = computed(() => runtimeConfig.public.imgproxyUrl as string | undefined);

  const resizeMode = computed(() => (props.fit === 'contain' ? 'fit' : 'fill'));

  const pictureClass = computed(() => (props.fit === 'contain' ? 'w-full h-auto block' : 'w-full h-full object-cover'));
  const imgClass = computed(() => {
    const base = props.fit === 'contain' ? 'w-full h-auto block' : 'w-full h-full object-cover';
    return props.imageClass ? `${base} ${props.imageClass}` : base;
  });

  const mediaWidht = computed(() => {
    if (props.width) return Number(props.width);
    return props.media?.width;
  });
  const mediaHeight = computed(() => {
    if (props.height) return Number(props.height);
    return props.media?.height;
  });

  // --- Core Logic ---

  // 1. Responsive breakpoints, respecting both width and height.
  //    Dimensions are clamped to what the media cache allowlist accepts BEFORE
  //    being used as srcset width descriptors, so the advertised width always
  //    matches the width imgproxy actually returns.
  const responsiveBreakpoints = computed(() => {
    const baseBreakpoints = [320, 640, 768, 1024, 1280, 1536];
    const maxWidth = mediaWidht.value;
    const maxHeight = mediaHeight.value;
    const aspectRatio = maxWidth && maxHeight ? maxWidth / maxHeight : 1;

    const candidates = baseBreakpoints
      .filter(bp => !maxWidth || bp <= maxWidth)
      .map(width => ({ width, height: Math.round(width / aspectRatio) }));

    // Always offer the source's native size as the largest candidate.
    if (maxWidth && maxHeight) {
      candidates.push({ width: maxWidth, height: maxHeight });
    }

    // Clamp first, then dedupe: an oversized original and a breakpoint can
    // clamp onto the same size, and duplicate srcset candidates are dead weight.
    const seen = new Set<string>();
    const breakpoints: { width: number; height: number }[] = [];
    for (const candidate of candidates) {
      const [width, height] = clampDimensions(candidate.width, candidate.height);
      const key = `${width}x${height}`;
      if (seen.has(key)) continue;
      seen.add(key);
      breakpoints.push({ width, height });
    }

    return breakpoints.sort((a, b) => a.width - b.width);
  });

  // AVIF reaches the same perceptual quality as webp/jpeg at a much lower
  // quality number, so encoding it at the same q as the fallback (often 85)
  // wastes bytes — notably on the LCP image. Cap avif quality; leave the
  // webp/jpeg fallback as-is.
  const AVIF_QUALITY_CAP = 60;
  const qualityForFormat = (format: string) => (format === 'avif' ? Math.min(props.quality, AVIF_QUALITY_CAP) : props.quality);

  // 2. Build every variant for one format. srcset and the <img> fallback both
  //    read from this, so they can never disagree about resize mode or quality
  //    the way two separately-assembled option strings could.
  const variantsFor = (format: ImgproxyFormat) =>
    responsiveBreakpoints.value
      .map(breakpoint => ({
        width: breakpoint.width,
        url: buildImgproxyUrl(
          props.media?.url,
          buildResizeOption({
            mode: resizeMode.value,
            width: breakpoint.width,
            height: breakpoint.height,
            quality: qualityForFormat(format),
            format,
          }),
          imgproxyHost.value
        ),
      }))
      .filter(variant => variant.url !== '');

  const primaryVariants = computed(() => variantsFor(props.format));
  const avifVariants = computed(() => (props.format === 'avif' ? [] : variantsFor('avif')));

  const toSrcset = (variants: { width: number; url: string }[]) => variants.map(variant => `${variant.url} ${variant.width}w`).join(', ');

  // 3. srcsets and the fallback <img src>, all from the same source of truth.
  const avifSrcset = computed(() => toSrcset(avifVariants.value));
  const primarySrcset = computed(() => toSrcset(primaryVariants.value));
  const fallbackSrc = computed(() => primaryVariants.value[0]?.url ?? '');

  // 4. Final alt text
  const altText = computed(() => props.alt || props.media?.alternativeText || '');

  const emit = defineEmits(['load']);

  const handleLoad = () => {
    emit('load');
  };
</script>
